import { galateaWebRouter } from '@inkitt/contracts-gweb';
import { VISITOR_ID_HEADER } from '@inkitt/utils-web';
import { tsRestFetchApi, initClient } from '@ts-rest/core';
import { store } from '../../store/store';
import type { SessionInfo } from '../../types/WebUser';
import { SESSION_INFO_HEADER } from '../../utils/constants';
import { getSessionBootstrap } from '../session/bootstrap-client';

/**
 * Resolves the identity headers for an outgoing gwebApi request. On the anon shell
 * (DEV-5533) the Redux store starts with an empty visitorId until SessionBootstrap/SessionRefresher
 * resolve it, so a request fired before that (e.g. the first pageview's PageViewTracker) would
 * otherwise go out identity-less, landing under a phantom visitor+session server-side
 * (DEV-5533 final-review C2). `getSessionBootstrap()` is the same memoized-per-page-load promise
 * every other consumer (SessionBootstrap, SessionRefresher, CookieConsentWithIdentity) awaits, so
 * this never triggers an extra network round trip beyond the first bootstrap fetch — and that fetch
 * itself uses plain `fetch()` (bootstrap-client.ts), never gwebApi, so there is no risk of this
 * function's own await looping back into itself (including for the `/session/refresh` request that
 * also flows through gwebApi).
 */
const resolveIdentityHeaders = async (): Promise<{ visitorId: string; sessionInfo: SessionInfo }> => {
  const initial = store.getState().user;
  if (initial.visitorId) {
    return { visitorId: initial.visitorId, sessionInfo: initial.sessionInfo };
  }

  const bootstrap = await getSessionBootstrap().catch(() => undefined);
  if (!bootstrap) {
    // No identity resolvable this page load — same fail-open as today; the request goes out
    // identity-less rather than blocking forever.
    return { visitorId: initial.visitorId, sessionInfo: initial.sessionInfo };
  }

  // Another consumer's SessionBootstrap dispatch may have landed onto the store while we were
  // awaiting the shared promise above — prefer that (it's the single source of truth going
  // forward) and fall back to the bootstrap response directly if nothing dispatched it yet.
  const afterBootstrap = store.getState().user;
  if (afterBootstrap.visitorId) {
    return { visitorId: afterBootstrap.visitorId, sessionInfo: afterBootstrap.sessionInfo };
  }
  return { visitorId: bootstrap.visitorId, sessionInfo: bootstrap.sessionInfo };
};

export const gwebApi = initClient(galateaWebRouter, {
  baseUrl: '', // NextJS self server
  baseHeaders: {},
  jsonQuery: true,
  api: async (args) => {
    const { visitorId, sessionInfo } = await resolveIdentityHeaders();

    return await tsRestFetchApi({
      ...args,
      headers: {
        ...args.headers,
        [VISITOR_ID_HEADER]: visitorId,
        [SESSION_INFO_HEADER]: JSON.stringify(sessionInfo),
      },
    });
  },
});
